import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import poolWithdrawalProvider, {
	type ApproveWithdrawalParams,
	type CancelWithdrawalParams,
	type CreatePoolWithdrawalParams,
	type ListPoolWithdrawalsParams,
	type PaginatedPoolWithdrawalsResponse,
	type PoolWithdrawal,
	type RejectWithdrawalParams,
} from "src/providers/pool-withdrawal-provider";

export function usePoolWithdrawals(params: ListPoolWithdrawalsParams = {}) {
	return useQuery<PaginatedPoolWithdrawalsResponse>({
		queryKey: ["pool-withdrawals", params],
		queryFn: () => poolWithdrawalProvider.listWithdrawals(params),
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
}

export function usePendingPoolWithdrawals(limit = 50, offset = 0) {
	return useQuery<PaginatedPoolWithdrawalsResponse>({
		queryKey: ["pool-withdrawals-pending", { limit, offset }],
		queryFn: () => poolWithdrawalProvider.listPendingApprovals(limit, offset),
		staleTime: 30_000,
		refetchInterval: 30_000,
	});
}

export function usePoolWithdrawal(id: number | null) {
	return useQuery<PoolWithdrawal>({
		queryKey: ["pool-withdrawal", id],
		queryFn: () => {
			if (id === null) {
				return Promise.reject(new Error("Withdrawal ID is required"));
			}
			return poolWithdrawalProvider.getWithdrawal(id);
		},
		enabled: id !== null,
		staleTime: 30_000,
	});
}

export function useCreatePoolWithdrawal() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: CreatePoolWithdrawalParams) =>
			poolWithdrawalProvider.createWithdrawal(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals"] });
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals-pending"] });
		},
	});
}

export function useApprovePoolWithdrawal() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: ApproveWithdrawalParams) =>
			poolWithdrawalProvider.approveWithdrawal(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals"] });
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals-pending"] });
		},
	});
}

export function useRejectPoolWithdrawal() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: RejectWithdrawalParams) =>
			poolWithdrawalProvider.rejectWithdrawal(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals"] });
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals-pending"] });
		},
	});
}

export function useCancelPoolWithdrawal() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: CancelWithdrawalParams) =>
			poolWithdrawalProvider.cancelWithdrawal(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals"] });
			queryClient.invalidateQueries({ queryKey: ["pool-withdrawals-pending"] });
		},
	});
}
